import React, { useEffect, useRef, useState } from 'react';

import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import AptIcon from 'assets/salesNew/apt.svg';
import Input from 'common-ui/input';
import CustomSelectComponent from 'common-ui/select/Select';
import { getWorkflowStatuses } from 'features/newsales/newsalesActions';
import { selectWorkflowStatuses } from 'features/newsales/newsalesSlice';
import MyPhoneInput from 'pages/components/myPhoneInput';

import { useLeadForm } from '../LeadFormContext';
import { CONTACT_INFO_B2B_DEFAULT_VALUES } from '../salesNewConstants';
import '../salesNewConstants';
import {
  AptImg,
  Container,
  ErrorMessage,
  InputGroup,
  InputWrapper,
  Label,
  MainInfoContent,
  Title,
} from './ContactInfoB2B.styles';

const ContactInfoB2B = ({ onValidationChange, shouldValidate }) => {
  const dispatch = useDispatch();
  const { formData, updateFormData } = useLeadForm();
  const workflowStatuses = useSelector(selectWorkflowStatuses);

  const { control, watch, reset } = useForm({
    defaultValues: CONTACT_INFO_B2B_DEFAULT_VALUES,
  });

  const [errors, setErrors] = useState({
    phone: '',
    companyName: '',
    status: '',
  });
  const [touched, setTouched] = useState({
    phone: false,
    companyName: false,
    status: false,
  });

  const formValues = watch();

  const prevUpdateRef = useRef({});
  const prevValidRef = useRef();

  useEffect(() => {
    const selectedStatus = formData.workflowStatusId
      ? workflowStatuses.find((s) => s.id === formData.workflowStatusId)
      : null;

    reset({
      companyName: formData.companyName || '',
      description: formData.description || '',
      webAddress: formData.webAddress || '',
      numberOfEmployees: formData.numberOfEmployees || '',
      phone: { value: formData.phoneNumber || '' },
      status: selectedStatus ? { value: selectedStatus.id, label: selectedStatus.name } : null,
    });
  }, [workflowStatuses]); // Only run when workflow statuses are loaded

  useEffect(() => {
    dispatch(getWorkflowStatuses({ page: 1, limit: 100 }));
  }, [dispatch]);

  const statusOptions = workflowStatuses
    ? workflowStatuses.map((status) => ({
        value: status.id,
        label: status.name,
      }))
    : [];

  useEffect(() => {
    const updates = {
      phoneNumber: formValues.phone?.value || '',
      companyName: formValues.companyName || '',
      description: formValues.description || '',
      webAddress: formValues.webAddress || '',
      numberOfEmployees: formValues.numberOfEmployees || '',
      workflowStatusId: formValues.status?.value || null,
    };

    const prev = prevUpdateRef.current;

    const isSame =
      prev.phoneNumber === updates.phoneNumber &&
      prev.companyName === updates.companyName &&
      prev.description === updates.description &&
      prev.webAddress === updates.webAddress &&
      prev.numberOfEmployees === updates.numberOfEmployees &&
      prev.workflowStatusId === updates.workflowStatusId;

    if (!isSame) {
      updateFormData(updates);
      prevUpdateRef.current = updates;
    }
  }, [
    formValues.phone?.value,
    formValues.companyName,
    formValues.description,
    formValues.webAddress,
    formValues.numberOfEmployees,
    formValues.status?.value,
  ]);

  const validateForm = () => {
    const newErrors = {
      phone: '',
      companyName: '',
      status: '',
    };

    const phoneValue = formValues.phone?.value || '';
    if (!phoneValue || phoneValue.length < 10) {
      newErrors.phone = 'Valid phone number is required';
    }

    if (!formValues.companyName || formValues.companyName.trim() === '') {
      newErrors.companyName = 'Company name is required';
    }

    if (!formValues.status) {
      newErrors.status = 'Status is required';
    }

    setErrors(newErrors);

    const isValid =
      phoneValue &&
      phoneValue.length >= 10 &&
      formValues.companyName &&
      formValues.companyName.trim() !== '' &&
      formValues.status;

    return isValid;
  };

  useEffect(() => {
    const isValid = validateForm();

    if (prevValidRef.current !== isValid) {
      prevValidRef.current = isValid;
      onValidationChange?.(isValid);
    }
  }, [
    formValues.phone?.value,
    formValues.companyName,
    formValues.description,
    formValues.webAddress,
    formValues.numberOfEmployees,
    formValues.status?.value,
  ]);

  useEffect(() => {
    if (shouldValidate) {
      setTouched({
        phone: true,
        companyName: true,
        status: true,
      });
    }
  }, [shouldValidate]);

  return (
    <Container>
      <Title>Add Lead</Title>
      <MainInfoContent>
        <InputGroup className="margin-none">
          <InputWrapper className="phone-select">
            <Label>
              Phone number <span>*</span>
            </Label>
            <Controller
              control={control}
              name="phone"
              render={({ field }) => (
                <MyPhoneInput
                  value={field.value?.value || ''}
                  onChange={(phone) => field.onChange({ ...field.value, value: phone })}
                  onBlur={() => setTouched((prev) => ({ ...prev, phone: true }))}
                />
              )}
            />
            {touched.phone && errors.phone && <ErrorMessage>{errors.phone}</ErrorMessage>}
          </InputWrapper>
          <InputWrapper className="name-input">
            <Label>
              Company name <span>*</span>
            </Label>
            <Controller
              control={control}
              name="companyName"
              render={({ field }) => (
                <Input
                  {...field}
                  type="text"
                  placeholder="e.g., Fnet Telecom"
                  onBlur={() => setTouched((prev) => ({ ...prev, companyName: true }))}
                />
              )}
            />
            {touched.companyName && errors.companyName && (
              <ErrorMessage>{errors.companyName}</ErrorMessage>
            )}
          </InputWrapper>
        </InputGroup>
        <InputGroup>
          <InputWrapper className="name-input">
            <Label>
              Description <AptImg src={AptIcon} alt="info" />
            </Label>
            <Controller
              control={control}
              name="description"
              render={({ field }) => (
                <Input {...field} type="text" placeholder="Description" />
              )}
            />
          </InputWrapper>
        </InputGroup>
        <InputGroup>
          <InputWrapper className="name-input patronymic">
            <Label>
              Web Address <AptImg src={AptIcon} alt="info" />
            </Label>
            <Controller
              control={control}
              name="webAddress"
              render={({ field }) => (
                <Input {...field} type="text" placeholder="http/website.com" />
              )}
            />
          </InputWrapper>
          <InputWrapper className="name-input patronymic">
            <Label>
              Number of employees <AptImg src={AptIcon} alt="info" />
            </Label>
            <Controller
              control={control}
              name="numberOfEmployees"
              render={({ field }) => <Input {...field} type="number" placeholder="123" />}
            />
          </InputWrapper>
        </InputGroup>
        <InputGroup>
          <InputWrapper className="patronymic">
            <Label>
              Status <span>*</span>
            </Label>
            <Controller
              control={control}
              name="status"
              render={({ field }) => (
                <CustomSelectComponent
                  label="Status"
                  req={true}
                  options={statusOptions}
                  value={field.value}
                  onChange={(option) => {
                    field.onChange(option);
                    setTouched((prev) => ({ ...prev, status: true }));
                  }}
                  onBlur={() => setTouched((prev) => ({ ...prev, status: true }))}
                  placeholder="Select status..."
                />
              )}
            />
            {touched.status && errors.status && <ErrorMessage>{errors.status}</ErrorMessage>}
          </InputWrapper>
        </InputGroup>
      </MainInfoContent>
    </Container>
  );
};

export default ContactInfoB2B;
